import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { debtPayoffService } from '../../services/debtPayoffService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import UpgradeModal from '../../components/ui/UpgradeModal';
import DebtCard from './components/DebtCard';
import AddDebtModal from './components/AddDebtModal';
import EditDebtModal from './components/EditDebtModal';
import PayoffStrategyModal from './components/PayoffStrategyModal';
import PaymentHistoryModal from './components/PaymentHistoryModal';
import WhatIfAnalysis from './components/WhatIfAnalysis';
import SavedPlansManager from './components/SavedPlansManager';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })?.format(amount);
};

const DebtPayoffTracker = () => {
  const { user } = useAuth();
  const { hasProAccess } = useSubscription();
  const navigate = useNavigate();
  const [debts, setDebts] = useState([]);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDebt, setEditingDebt] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiOptimization, setAiOptimization] = useState(null);
  const [viewingPlansForDebt, setViewingPlansForDebt] = useState(null);
  const [selectedDebts, setSelectedDebts] = useState([]);
  const [comparisonMode, setComparisonMode] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!hasProAccess) {
      setShowUpgradeModal(true);
      return;
    }

    loadDebtData();
  }, [user, hasProAccess, navigate]);

  const loadDebtData = async () => {
    try {
      setLoading(true);
      setError('');
      const [debtsData, summary] = await Promise.all([
        debtPayoffService?.getAll(),
        debtPayoffService?.getPortfolioSummary(user?.id)
      ]);
      setDebts(debtsData);
      setPortfolioSummary(summary);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDebt = async (debtData) => {
    try {
      await debtPayoffService?.create(debtData);
      setShowAddModal(false);
      await loadDebtData();
    } catch (err) {
      throw err;
    }
  };

  const handleEditDebt = async (debtId, updates) => {
    try {
      await debtPayoffService?.update(debtId, updates);
      setEditingDebt(null);
      await loadDebtData();
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteDebt = async (debtId) => {
    if (!confirm('Are you sure you want to delete this debt? This action cannot be undone.')) {
      return;
    }

    try {
      await debtPayoffService?.delete(debtId);
      await loadDebtData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleGenerateStrategy = async () => {
    if (!hasProAccess) {
      setShowUpgradeModal(true);
      return;
    }

    try {
      setLoadingAI(true);
      setAiError('');
      // Remove or comment out the undefined service call
      // const result = await aiDebtOptimizationService?.generateOptimizationStrategy(user?.id);
      // setAiOptimization(result?.optimization);
      // setShowStrategyModal(true);
      // await loadDebtData();
      
      // Temporary placeholder - service not imported
      setAiError('AI optimization service is not yet configured');
    } catch (err) {
      setAiError(err?.message);
    } finally {
      setLoadingAI(false);
    }
  };

  const handleViewPaymentHistory = (debt) => {
    setSelectedDebt(debt);
  };

  const handleViewSavedPlans = (debt) => {
    setViewingPlansForDebt(debt);
    setSelectedDebts([]);
  };

  const handleToggleDebtSelection = (debt) => {
    setSelectedDebts(prev => {
      const isSelected = prev?.some(d => d?.id === debt?.id);
      if (isSelected) {
        return prev?.filter(d => d?.id !== debt?.id);
      } else {
        return [...prev, debt];
      }
    });
  };

  const handleCompareSelected = () => {
    if (selectedDebts?.length < 2) {
      setError('Please select at least 2 debts to compare');
      return;
    }
    setViewingPlansForDebt(null);
    setComparisonMode(true);
  };

  const handleExitComparison = () => {
    setComparisonMode(false);
    setSelectedDebts([]);
  };

  const activeDebts = debts?.filter(d => d?.status === 'active') || [];
  const paidOffDebts = debts?.filter(d => d?.status === 'paid_off') || [];

  const projectedPayoffDate = () => {
    if (!portfolioSummary || portfolioSummary?.totalDebt === 0) return 'N/A';
    
    const monthlyPayment = portfolioSummary?.totalMinimumPayment;
    const monthlyInterest = portfolioSummary?.totalMonthlyInterest;
    
    if (monthlyPayment <= monthlyInterest) return 'Never (payment < interest)';
    
    const effectivePayment = monthlyPayment - monthlyInterest;
    const months = Math.ceil(portfolioSummary?.totalDebt / effectivePayment);
    
    const payoffDate = new Date();
    payoffDate?.setMonth(payoffDate?.getMonth() + months);
    
    return `${payoffDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} (${months} months)`;
  };

  if (!user) return null;

  if (!hasProAccess) {
    return (
      <>
        <Header />
        <main className="max-w-7xl mx-auto px-16 py-24 md:px-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-24 gap-16">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-8">
                Debt Payoff Tracker
              </h1>
              <p className="text-muted-foreground">
                Track your debt payoff progress
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-48">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Portfolio Summary for Free Users */}
              {portfolioSummary && activeDebts?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16 mb-32">
                  <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                    <div className="flex items-center gap-12 mb-12">
                      <div className="w-40 h-40 rounded-lg bg-destructive/10 flex items-center justify-center">
                        <Icon name="TrendingDown" size={20} color="var(--color-destructive)" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Debt</p>
                        <p className="text-2xl font-bold text-foreground">
                          {formatCurrency(portfolioSummary?.totalDebt)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                    <div className="flex items-center gap-12 mb-12">
                      <div className="w-40 h-40 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="DollarSign" size={20} color="var(--color-primary)" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Monthly Payment</p>
                        <p className="text-2xl font-bold text-foreground">
                          {formatCurrency(portfolioSummary?.totalMinimumPayment)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                    <div className="flex items-center gap-12 mb-12">
                      <div className="w-40 h-40 rounded-lg bg-success/10 flex items-center justify-center">
                        <Icon name="Calendar" size={20} color="var(--color-success)" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Payoff Date</p>
                        <p className="text-lg font-bold text-foreground">
                          {projectedPayoffDate()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* What-If Analysis - Locked for Free Users */}
              <WhatIfAnalysis 
                debts={debts} 
                portfolioSummary={portfolioSummary}
                isPro={false}
              />

              {/* Upgrade CTA */}
              <div className="mt-24 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-24 border border-primary/20">
                <div className="flex flex-col md:flex-row items-center justify-between gap-16">
                  <div className="flex items-center gap-16">
                    <div className="w-56 h-56 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Crown" size={28} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-4">
                        Unlock Pro Features
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Get full access to debt management, what-if analysis, and smart recommendations
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => setShowUpgradeModal(true)} size="lg">
                    <Icon name="Zap" size={18} className="mr-8" />
                    Upgrade to Pro
                  </Button>
                </div>
              </div>
            </>
          )}
        </main>
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="Debt Payoff Tracker"
          limit={0}
          current={0}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-16 py-24 md:px-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-24 gap-16">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-8">
              Debt Payoff Tracker
            </h1>
            <p className="text-muted-foreground">
              Manage your debts and track payoff progress
            </p>
          </div>
          <div className="flex gap-12">
            <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-8">
              <Icon name="Plus" size={18} />
              Add Debt
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-24 p-16 bg-destructive/10 border border-destructive rounded-lg">
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {aiError && (
          <div className="mb-24 p-16 bg-warning/10 border border-warning rounded-lg">
            <p className="text-warning">{aiError}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-48">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {portfolioSummary && activeDebts?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-32">
                <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-12 mb-12">
                    <div className="w-40 h-40 rounded-lg bg-destructive/10 flex items-center justify-center">
                      <Icon name="TrendingDown" size={20} color="var(--color-destructive)" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Debt</p>
                      <p className="text-2xl font-bold text-foreground">
                        {formatCurrency(portfolioSummary?.totalDebt)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-12 mb-12">
                    <div className="w-40 h-40 rounded-lg bg-warning/10 flex items-center justify-center">
                      <Icon name="Percent" size={20} color="var(--color-warning)" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Avg Interest Rate</p>
                      <p className="text-2xl font-bold text-foreground">
                        {portfolioSummary?.weightedAvgInterestRate?.toFixed(2)}%
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-12 mb-12">
                    <div className="w-40 h-40 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="DollarSign" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Monthly Payment</p>
                      <p className="text-2xl font-bold text-foreground">
                        {formatCurrency(portfolioSummary?.totalMinimumPayment)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-12 mb-12">
                    <div className="w-40 h-40 rounded-lg bg-success/10 flex items-center justify-center">
                      <Icon name="Calendar" size={20} color="var(--color-success)" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Projected Payoff</p>
                      <p className="text-lg font-bold text-foreground">
                        {projectedPayoffDate()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeDebts?.length === 0 && paidOffDebts?.length === 0 ? (
              <div className="text-center py-48">
                <div className="w-64 h-64 mx-auto mb-16 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="TrendingDown" size={32} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-8">No Debts Tracked</h3>
                <p className="text-muted-foreground mb-24">
                  Start tracking your debts to get AI-powered payoff strategies
                </p>
                <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-8 mx-auto">
                  <Icon name="Plus" size={18} />
                  Add Your First Debt
                </Button>
              </div>
            ) : (
              <>
                {activeDebts?.length > 0 && (
                  <div className="mb-32">
                    <WhatIfAnalysis 
                      debts={debts} 
                      portfolioSummary={portfolioSummary}
                      isPro={hasProAccess}
                    />
                  </div>
                )}

                {/* Debt Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20 md:gap-24">
                  {activeDebts?.map((debt) => (
                    <div key={debt?.id} className="relative">
                      {hasProAccess && (
                        <div className="absolute top-12 right-12 z-10">
                          <input
                            type="checkbox"
                            checked={selectedDebts?.some(d => d?.id === debt?.id)}
                            onChange={() => handleToggleDebtSelection(debt)}
                            className="w-20 h-20 rounded border-border cursor-pointer"
                            aria-label={`Select ${debt?.debtName}`}
                          />
                        </div>
                      )}
                      <DebtCard
                        debt={debt}
                        onEdit={() => setEditingDebt(debt)}
                        onDelete={() => handleDeleteDebt(debt?.id)}
                        onViewHistory={() => handleViewPaymentHistory(debt)}
                        onViewPlans={() => handleViewSavedPlans(debt)}
                      />
                    </div>
                  ))}
                </div>

                {/* Multi-Debt Comparison Actions */}
                {selectedDebts?.length > 0 && (
                  <div className="mt-24 p-20 bg-primary/10 border border-primary rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-12">
                        <Icon name="CheckSquare" size={20} color="var(--color-primary)" />
                        <span className="text-sm font-semibold text-foreground">
                          {selectedDebts?.length} debt{selectedDebts?.length !== 1 ? 's' : ''} selected
                        </span>
                      </div>
                      <div className="flex gap-12">
                        <Button
                          onClick={handleCompareSelected}
                          size="sm"
                          disabled={selectedDebts?.length < 2}
                        >
                          <Icon name="BarChart2" size={16} className="mr-8" />
                          Compare Plans
                        </Button>
                        <Button
                          onClick={handleExitComparison}
                          size="sm"
                          variant="outline"
                        >
                          Clear Selection
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {paidOffDebts?.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-16">Paid Off Debts</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
                      {paidOffDebts?.map(debt => (
                        <DebtCard
                          key={debt?.id}
                          debt={debt}
                          onEdit={() => setEditingDebt(debt)}
                          onDelete={() => handleDeleteDebt(debt?.id)}
                          onViewHistory={() => handleViewPaymentHistory(debt)}
                          onViewPlans={() => handleViewSavedPlans(debt)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      {showAddModal && (
        <AddDebtModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddDebt}
        />
      )}

      {editingDebt && (
        <EditDebtModal
          debt={editingDebt}
          onClose={() => setEditingDebt(null)}
          onUpdate={handleEditDebt}
        />
      )}

      {showStrategyModal && aiOptimization && (
        <PayoffStrategyModal
          optimization={aiOptimization}
          onClose={() => setShowStrategyModal(false)}
        />
      )}

      {selectedDebt && (
        <PaymentHistoryModal
          debt={selectedDebt}
          onClose={() => setSelectedDebt(null)}
        />
      )}

      {viewingPlansForDebt && (
        <SavedPlansManager
          debt={viewingPlansForDebt}
          selectedDebts={comparisonMode ? selectedDebts : []}
          onClose={() => {
            setViewingPlansForDebt(null);
            setComparisonMode(false);
          }}
        />
      )}

      {comparisonMode && selectedDebts?.length > 0 && (
        <SavedPlansManager
          debt={null}
          selectedDebts={selectedDebts}
          onClose={handleExitComparison}
        />
      )}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="Debt Payoff Tracker"
        limit={0}
        current={0}
      />
    </div>
  );
};

export default DebtPayoffTracker;